import React, { Suspense } from 'react'
import { Navigate, useRoutes } from 'react-router-dom'
import { DashboardLayout } from './components/DashboardLayout/DashboardLayout'
import { DashboardErrorView } from './components/ErrorBoundary/DashboardErrorView'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import { ErrorContent } from './components/ErrorContent/ErrorContent'
import { LoadingListPage } from './components/LoadingListPage/LoadingListPage'
import { LoadingScreen } from './components/LoadingScreen/LoadingScreen'
import { DEPRECATED_ROUTES, ROUTES } from './constants/routes'
import { ProvideAuth, useProvideAuth } from './hooks/useAuth'
import { ProvideLoadingWarning } from './hooks/useLoadingWarning'
import { useRouter } from './hooks/useRouter'
import { ProvideToast } from './hooks/useToast'
import { DashboardRoutes } from './pages/Dashboard/DashboardRoutes'

export const TeamRoutes = React.lazy(() =>
  import('./pages/Team/TeamRoutes').then((module) => ({
    default: module.TeamRoutes,
  })),
)
export const FinancialRoutes = React.lazy(() =>
  import('./pages/Financials/FinancialRoutes').then((module) => ({
    default: module.FinancialRoutes,
  })),
)
export const CampRoutes = React.lazy(() =>
  import('./pages/Events/CampRoutes').then((module) => ({
    default: module.CampRoutes,
  })),
)
export const EventRoutes = React.lazy(() =>
  import('./pages/Events/EventRoutes').then((module) => ({
    default: module.EventRoutes,
  })),
)
export const ContactRoutes = React.lazy(() =>
  import('./pages/Contacts/ContactRoutes').then((module) => ({
    default: module.ContactRoutes,
  })),
)
export const AttendeeRoutes = React.lazy(() =>
  import('./pages/Attendees/AttendeeRoutes').then((module) => ({
    default: module.AttendeeRoutes,
  })),
)
export const MarketingRoutes = React.lazy(() =>
  import('./pages/Marketing/MarketingRoutes').then((module) => ({
    default: module.MarketingRoutes,
  })),
)
export const InboxRoutes = React.lazy(() =>
  import('./pages/Inbox/InboxRoutes').then((module) => ({
    default: module.InboxRoutes,
  })),
)
export const SettingRoutes = React.lazy(() =>
  import('./pages/Settings/SettingRoutes').then((module) => ({
    default: module.SettingRoutes,
  })),
)

const LoadingDashboard = () => (
  <DashboardLayout>
    <LoadingListPage />
  </DashboardLayout>
)

export const AppRoutes = () => {
  const { search } = useRouter()
  const programId = search.program_id?.toString()
  const auth = useProvideAuth({ programId })
  const { isLoading, isError, error } = auth
  const element = useRoutes([
    { index: true, element: <Navigate to={ROUTES.DASHBOARD} replace /> },
    {
      path: `${ROUTES.TEAM}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <TeamRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.FINANCIALS}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <FinancialRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${DEPRECATED_ROUTES.CAMPS}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <CampRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.EVENTS}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <EventRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.CONTACTS}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <ContactRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.ATTENDEES}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <AttendeeRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.MARKETING}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <MarketingRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.INBOX}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <InboxRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `${ROUTES.SETTINGS}/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <Suspense fallback={<LoadingDashboard />}>
            <SettingRoutes />
          </Suspense>
        </ErrorBoundary>
      ),
    },
    {
      path: `/*`,
      element: (
        <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
          <DashboardRoutes />
        </ErrorBoundary>
      ),
    },
  ])

  if (isError && (!error?.code || error?.code >= 500)) {
    return (
      <DashboardLayout>
        <div className="d-flex" style={{ minHeight: '90vh' }}>
          <ErrorContent className="my-md-auto w-100 mt-5" error={error}>
            <a
              className="btn btn-link"
              href="https://citruscamps.atlassian.net/servicedesk/customer/portals"
            >
              Contact Support
            </a>{' '}
            ·{' '}
            <a className="btn btn-link" href="https://citruscamps.statuspage.io/">
              Status Page
            </a>
          </ErrorContent>
        </div>
      </DashboardLayout>
    )
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <ProvideAuth programId={programId}>
      <ProvideToast>
        <ProvideLoadingWarning>{element}</ProvideLoadingWarning>
      </ProvideToast>
    </ProvideAuth>
  )
}
